<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { shouldShowEditableTitle } from '@/utils/editorTitleVisibility'
import {
    getTitleDefaultFontSizeClass,
    getTitleDefaultFontSizePx,
    getTitleDefaultFontWeight,
    getTitleDefaultTextAlign,
} from '@/utils/editor-title-defaults'
import {
    TITLE_FRAME_CONTENT_MAX_BYTES,
    normalizeTitleFrameContent,
} from '@/utils/editor-payload'

const props = defineProps<{
    element: any
    extra: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    isDarkMode: boolean
    isFull: boolean
    isNew?: boolean
    isNewBefore?: boolean
    isChild?: boolean
    elementParent?: any
    elementParentIndex?: number
    isEditable?: boolean
    sectionHasBackgroundImage?: boolean
    schema?: any
    headingStyle?: Record<string, string>
}>()

const betaStore = useEditorBetaStore()
const defaultStore = useEditorDefaultStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()

const content = ref(props.element.frameContent)
const editorTitleTextarea = ref<HTMLTextAreaElement | null>(null)

watch(
    () => props.element.frameContent,
    value => {
        content.value = value
    },
)

const hasExtra = computed(() => props.extra != null)
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))

const dynamicStyleMargin = computed(() => {
    if (!hasExtra.value) return

    return {
        margin: `${props.extra.margin?.top ?? 0}px ${props.extra.margin?.right ?? 0}px ${props.extra.margin?.bottom ?? 0}px ${props.extra.margin?.left ?? 0}px`,
        padding: `${props.extra.padding?.top ?? 0}px ${props.extra.padding?.right ?? 0}px ${props.extra.padding?.bottom ?? 0}px ${props.extra.padding?.left ?? 0}px`,
    }
})

const isCustomParentBackgroundColor = computed(() => {
    return props.elementParent?.extra?.background_color && props.elementParent?.extra?.background_color != ''
})

const titleColor = computed(() => {
    if (hasExtra.value && props.extra.style?.color) return props.extra.style?.color
    if (props.sectionHasBackgroundImage) return layoutStore.getSectionTitleColorInverted()
    if (isCustomParentBackgroundColor.value) return layoutStore.getAdaptiveTitleColor(props.elementParent.extra?.background_color)

    const backgroundExtra = editorPageStore.getSectionExtra(props.sectionIndex)?.background_color

    if (backgroundExtra) return layoutStore.getAdaptiveTitleColor(backgroundExtra)

    return layoutStore.getSectionTitleColorDefault(props.sectionIndex)
})

const section = computed(() => editorPageStore.activePage?.content?.[props.sectionIndex])
const showEditableTitle = computed(() => {
    return shouldShowEditableTitle({
        sectionComponent: section.value?.component,
        elementParentFrameType: props.elementParent?.frameType,
    })
})
const sectionIsMainContent = computed(() => section.value?.component?.includes('maincontent'))
const isPrimaryHomeMainContentSection = computed(() => {
    if (!editorPageStore.activePage?.isHome) return false

    const firstSection = editorPageStore.activePage?.content?.[0]?.component
    const firstEditableSectionIndex = firstSection?.includes('home') ? 1 : 0

    return props.sectionIndex === firstEditableSectionIndex
})

const hideTitleInHomeMainContent = computed(() => {
    if (!editorPageStore.activePage?.isHome) return false
    if (!sectionIsMainContent.value) return false
    if (props.elementParent?.frameType === 'container') return false

    return isPrimaryHomeMainContentSection.value && props.elementIndex === 0
})
const isTopLevelTitle = computed(() => {
    if (props.elementParent?.frameType) return false
    if (!Array.isArray(props.schema) || props.schema.length !== 2) return false

    const currentLevel = props.schema[props.schema.length - 1]

    return currentLevel?.element === 'title'
})
const firstTopLevelTitleIndex = computed(() => {
    const sectionContent = section.value?.frameContent

    if (!Array.isArray(sectionContent)) return null

    return sectionContent.findIndex((frame: any) => frame?.frameType === 'title')
})
const isSiteClientManagedPrimaryTitle = computed(() => {
    if (!isTopLevelTitle.value) return false

    return firstTopLevelTitleIndex.value !== -1
        && firstTopLevelTitleIndex.value !== null
        && props.elementIndex === firstTopLevelTitleIndex.value
})
const defaultTitleFontSizeClass = computed(() => getTitleDefaultFontSizeClass(
    editorPageStore.getSectionExtra(props.sectionIndex),
    defaultStore.newSection.extra,
))
const defaultTitleFontSize = computed(() => getTitleDefaultFontSizePx(
    editorPageStore.getSectionExtra(props.sectionIndex),
    defaultStore.newSection.extra,
    props.extra?.style?.fontSize,
))
const defaultTitleFontWeight = computed(() => getTitleDefaultFontWeight(
    editorPageStore.getSectionExtra(props.sectionIndex),
    layoutStore.getSectionFontWeightDefault(props.sectionIndex) || props.extra?.style?.fontWeight,
))
const defaultTitleTextAlign = computed(() => getTitleDefaultTextAlign(
    editorPageStore.getSectionExtra(props.sectionIndex),
    props.extra?.style?.textAlign,
))
const shouldUseInheritedFontSize = computed(() => {
    if (isSiteClientManagedPrimaryTitle.value) return true
    if (props.extra?.style_font_size_lock === true) return true

    return !props.extra?.style?.fontSize
})
const shouldUseInheritedFontWeight = computed(() => {
    if (props.extra?.style_font_weight_lock === true) return true

    return !props.extra?.style?.fontWeight
})
const shouldUseInheritedTextAlign = computed(() => {
    if (props.extra?.style_text_align_lock === true) return true

    return !props.extra?.style?.textAlign
})

const dynamicStyle = computed(() => {
    if (!hasExtra.value) return

    const style: any = {
        color: titleColor.value,
    }

    if (!shouldUseInheritedFontSize.value) {
        style.fontSize = props.extra.style?.fontSize
    } else if (!isSiteClientManagedPrimaryTitle.value && props.elementIndex !== 0) {
        style.fontSize = defaultTitleFontSize.value
    }

    style.fontWeight = shouldUseInheritedFontWeight.value
        ? (props.headingStyle?.['font-weight'] || defaultTitleFontWeight.value)
        : props.extra.style?.fontWeight

    style.textAlign = shouldUseInheritedTextAlign.value
        ? (props.headingStyle?.['text-align'] || defaultTitleTextAlign.value)
        : props.extra.style?.textAlign

    return style
})

const resolvedTitleClass = computed(() => {
    if (shouldUseInheritedFontSize.value) {
        return props.headingStyle?.['font-size'] || defaultTitleFontSizeClass.value || ''
    }

    return ''
})

const syncTitleTextareaHeight = async () => {
    await nextTick()

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    }

    const textarea = editorTitleTextarea.value

    if (!textarea) return

    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
}

const onChangeTitle = () => {
    const normalizedContent = normalizeTitleFrameContent(content.value)

    if (normalizedContent !== content.value) {
        content.value = normalizedContent
    }

    betaStore.patchElementBySchema(props.schema, {
        frameContent: normalizedContent,
    })
    syncTitleTextareaHeight()
}

const onFocusTitle = () => {
    betaStore.setSelectedElementBySchema(props.schema, props.element)
}

watch([content, dynamicStyle, resolvedTitleClass, () => betaStore.previewDevice], () => {
    syncTitleTextareaHeight()
}, { deep: true })

onMounted(() => {
    syncTitleTextareaHeight()
})
</script>

<template>
    <div
        tabindex="0"
        class="editor-title"
        :data-active="isActive"
        :style="dynamicStyleMargin"
        :schema="JSON.stringify(schema)"
    >
        <textarea
            v-if="showEditableTitle && !hideTitleInHomeMainContent"
            ref="editorTitleTextarea"
            v-model="content"
            :class="resolvedTitleClass"
            placeholder="Adicione seu título aqui..."
            :style="dynamicStyle"
            :maxlength="TITLE_FRAME_CONTENT_MAX_BYTES"
            rows="1"
            @focus="onFocusTitle"
            @input="onChangeTitle"
            @keydown.enter.prevent
        />
    </div>
</template>

<style scoped>
.editor-title {
    @apply relative flex border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-title[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}

.editor-title textarea {
    @apply h-auto min-h-[1.2em] w-full resize-none overflow-hidden break-words border-none bg-transparent outline-none;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}
</style>
